'use client';

import jsCookie from 'js-cookie';

import { getClientEnv } from '@/config/clientEnv';
import {
  REMEMBER_ME_SESSION_IDLE_DURATION_HOURS,
  REMEMBER_ME_SESSION_TRACKING_DAYS,
  REMEMBER_ME_SESSION_TRACKING_KEY,
} from '@/config/constants';

const getCookieValue = (
  cookieDomain: string,
  excludedDomains: string[] = [],
  isPetPro: boolean = false,
  logoutReason?: string
) => {
  const { CURRENT_ENV } = getClientEnv();

  const cookieData: any = {
    timestamp: Date.now().toString(),
    domain: cookieDomain,
    env: CURRENT_ENV,
    secure: false,
    expires: REMEMBER_ME_SESSION_TRACKING_DAYS,
    excludedDomains, // Domains where this session is no longer valid
    isPetPro, // Store whether this session was created for a PetPro user
  };

  // Add logout reason if provided (for idle timeout coordination)
  if (logoutReason) {
    cookieData.logoutReason = logoutReason;
    cookieData.logoutTimestamp = Date.now().toString();
  }

  return JSON.stringify(cookieData);
};

/**
 * Helper function to mark current domain as excluded in the session cookie
 * This allows the cookie to remain valid on other domains while being invalid on current domain
 */
const markCurrentDomainAsExcluded = (currentCookieValue: string) => {
  try {
    const cookieData = JSON.parse(currentCookieValue);
    const currentHost = window.location.host;

    // Get existing excluded domains or initialize empty array
    const excludedDomains = cookieData.excludedDomains || [];

    // Add current domain to excluded list if not already present
    if (!excludedDomains.includes(currentHost)) {
      excludedDomains.push(currentHost);
    }

    // Create updated cookie value with current domain excluded
    const updatedCookieValue = JSON.stringify({
      ...cookieData,
      excludedDomains,
      lastExcludedTimestamp: Date.now().toString(), // Track when exclusion happened
    });

    console.log(`Marking domain as excluded: ${currentHost}`);
    console.log(`Updated excluded domains: ${excludedDomains.join(', ')}`);

    return {
      success: true,
      updatedCookieValue,
      excludedDomain: currentHost,
      totalExcluded: excludedDomains.length,
    };
  } catch (error) {
    console.error('Error marking domain as excluded:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
    };
  }
};

/**
 * Utility function to handle user logout during idle timeout
 * This function will mark the session as idle expired but NOT remove the cookie
 * The cookie will be overwritten when the user logs in again
 */
export const handleIdleLogout = (isPetPro?: boolean) => {
  // For idle timeout, mark session as expired but don't remove the cookie
  // This allows all domains to detect the idle timeout
  markSessionAsIdleExpired();
  console.log('Session marked as idle expired (cookie preserved for cross-domain coordination)');

  // Determine logout URL based on current location
  const isBackoffice =
    window.location.pathname.includes('backoffice') ||
    window.location.host.includes('backoffice') ||
    window.location.host.includes('petpro');

  const logoutUrl = isBackoffice ? '/logout' : `/${window.PAWTREE_CURRENT_WEBALIAS || 'pawtree'}/account/logout`;

  // Redirect to logout page
  window.location.href = logoutUrl;
};

/**
 * Utility function to update the session tracking cookie when user becomes active
 * Respects the original cookie's domain and preserves excluded domains list
 */
export const updateSessionCookie = (isPetPro?: boolean) => {
  const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  if (sessionCookie) {
    let cookieDomain;
    let excludedDomains = [];
    let preservedIsPetPro = isPetPro || false; // Use provided value or default to false

    try {
      // Parse the cookie to get the original domain, excluded domains, and isPetPro flag
      const cookieData = JSON.parse(sessionCookie);
      cookieDomain = cookieData.domain;
      excludedDomains = cookieData.excludedDomains || [];
      preservedIsPetPro = cookieData.isPetPro || isPetPro || false; // Preserve existing isPetPro flag

      if (!cookieDomain) {
        // Fallback: if no domain in cookie, use current logic
        cookieDomain = `.${window.location.host.split('.').slice(-2).join('.')}`;
      }
    } catch (parseError) {
      // Fallback: if cookie isn't JSON, use current logic
      console.warn('Could not parse cookie domain, using fallback logic');
      cookieDomain = `.${window.location.host.split('.').slice(-2).join('.')}`;
    }

    // Update the cookie on the SAME domain where it was originally set, preserving excluded domains and isPetPro flag
    jsCookie.set(REMEMBER_ME_SESSION_TRACKING_KEY, getCookieValue(cookieDomain, excludedDomains, preservedIsPetPro), {
      domain: cookieDomain,
      secure: false,
      sameSite: 'Lax',
      expires: REMEMBER_ME_SESSION_TRACKING_DAYS,
    });

    console.log(`Session cookie updated on original domain: ${cookieDomain} (isPetPro: ${preservedIsPetPro})`);
    if (excludedDomains.length > 0) {
      console.log(`Preserved excluded domains: ${excludedDomains.join(', ')}`);
    }
  }
};

export const setSessionCookie = (isPetPro?: boolean) => {
  console.log('🍪 [setSessionCookie] Creating new session cookie for isPetPro:', isPetPro);

  // Check if there's an existing cookie that we're about to overwrite
  const existingCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
  if (existingCookie) {
    console.log('🗑️ [setSessionCookie] Overwriting existing cookie (including any idle timeout markers)');
    try {
      const existingData = JSON.parse(existingCookie);
      if (existingData.logoutReason === 'idle_timeout') {
        console.log('🧹 [setSessionCookie] Clearing idle timeout marker from previous session');
      }
      if (existingData.excludedDomains && existingData.excludedDomains.length > 0) {
        console.log('🧹 [setSessionCookie] Clearing domain exclusions from previous session');
      }
    } catch (e) {
      console.log('🧹 [setSessionCookie] Overwriting corrupted existing cookie');
    }
  }

  // For PetPro users, we always use root domain to track activity across all sites
  // For regular users, we use current domain only
  const rootDomain = window.location.host.split('.').slice(-2).join('.');
  let cookieDomain = `.${rootDomain}`;

  // ALWAYS start fresh with a completely new session
  // This overwrites any existing cookie including idle timeout markers and domain exclusions
  let excludedDomains: string[] = [];

  console.log('🧹 [setSessionCookie] Fresh login: Creating completely new session cookie');

  const cookieValue = getCookieValue(cookieDomain, excludedDomains, isPetPro || false);
  console.log('🔧 [setSessionCookie] Cookie value to set:', cookieValue);
  console.log('🌐 [setSessionCookie] Target domain:', cookieDomain);
  console.log('🌍 [setSessionCookie] Current hostname:', window.location.hostname);
  console.log('🌍 [setSessionCookie] Current host:', window.location.host);
  console.log('🌍 [setSessionCookie] Current protocol:', window.location.protocol);

  jsCookie.set(REMEMBER_ME_SESSION_TRACKING_KEY, cookieValue, {
    domain: cookieDomain,
    secure: false,
    sameSite: 'Lax',
    expires: REMEMBER_ME_SESSION_TRACKING_DAYS,
  });

  // Verify the cookie was actually set
  const verificationCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
  if (verificationCookie) {
    console.log('✅ [setSessionCookie] Cookie successfully created and verified');
    console.log('🍪 [setSessionCookie] Verification cookie:', verificationCookie);
  } else {
    console.error('❌ [setSessionCookie] Cookie creation failed - cookie not found after setting');
  }

  console.log('📋 [setSessionCookie] Session cookie set for domain:', cookieDomain, 'isPetPro:', isPetPro || false);
  console.log('📋 [setSessionCookie] Fresh session created - all previous markers cleared');
};

/**
 * Helper function to check if current domain is excluded from the session cookie
 */
const isCurrentDomainExcluded = (cookieValue: string): boolean => {
  try {
    const cookieData = JSON.parse(cookieValue);
    const excludedDomains = cookieData.excludedDomains || [];
    const currentHost = window.location.host;

    return excludedDomains.includes(currentHost);
  } catch (error) {
    // If we can't parse the cookie, assume it's not excluded
    return false;
  }
};

// Constants for error messages
const SESSION_NOT_FOUND_MESSAGE = 'Session cookie not found';
const CURRENT_DOMAIN_EXCLUDED_MESSAGE = 'Current domain is excluded from session';
const IDLE_TIMEOUT_MESSAGE = 'Session expired due to idle timeout on another domain';
const AUTO_CLEANING_PREFIX = 'Auto-cleaning';
const UNKNOWN_ERROR_MESSAGE = 'Unknown error';

/**
 * Utility function to check if the session tracking cookie has expired
 * Also checks if current domain is excluded from the session
 * @param autoCleanup - Whether to automatically remove expired cookies (default: true)
 * @returns {object} Object containing expiration status and details
 */
export const isSessionCookieExpired = (autoCleanup: boolean = true) => {
  const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  // If cookie doesn't exist, consider it expired
  if (!sessionCookie) {
    return {
      isExpired: false,
      exists: false,
      reason: SESSION_NOT_FOUND_MESSAGE,
      hoursElapsed: null,
      remainingHours: null,
    };
  }

  try {
    // Parse the JSON cookie first to check for idle timeout
    const cookieData = JSON.parse(sessionCookie);

    // PRIORITY 1: Check if session was marked as expired due to idle timeout
    // This takes precedence over domain exclusion because idle timeout should affect ALL domains
    if (cookieData.logoutReason === 'idle_timeout') {
      console.log('🏃‍♂️ [IdleTracking] Detected idle timeout from another domain - triggering logout');

      // Never remove the cookie during idle timeout detection
      // This ensures all domains can detect the idle timeout
      // The cookie will be overwritten when user logs in again
      return {
        isExpired: true,
        exists: true,
        reason: IDLE_TIMEOUT_MESSAGE,
        hoursElapsed: null,
        remainingHours: null,
        isExcluded: false,
        shouldLogout: true,
        wasIdleTimeout: true,
        autoCleanedUp: false, // Never auto-cleanup idle timeout cookies
      };
    }

    // PRIORITY 2: Check if current domain is excluded (only if no idle timeout)
    if (isCurrentDomainExcluded(sessionCookie)) {
      // Domain is excluded - this should trigger immediate logout
      // The IdleTracking component will handle the actual logout when it sees isExcluded = true
      return {
        isExpired: true,
        exists: true,
        reason: CURRENT_DOMAIN_EXCLUDED_MESSAGE,
        hoursElapsed: null,
        remainingHours: null,
        isExcluded: true,
        shouldLogout: true, // Signal that logout should be triggered
      };
    }

    // PRIORITY 3: Check normal session expiration based on timestamp
    const sessionTime = parseInt(cookieData.timestamp, 10);

    // Check if the timestamp is valid
    if (isNaN(sessionTime)) {
      // Auto-cleanup corrupted cookies if requested
      if (autoCleanup) {
        console.log(`${AUTO_CLEANING_PREFIX} corrupted session cookie (invalid timestamp)`);
        const cleanupResult = removeSessionCookie();

        return {
          isExpired: true,
          exists: false, // Cookie was removed
          reason: 'Invalid timestamp in cookie - auto-removed',
          hoursElapsed: null,
          remainingHours: null,
          autoCleanedUp: true,
          cleanupResult,
          shouldLogout: true, // Signal that logout should be triggered
        };
      }

      return {
        isExpired: true,
        exists: true,
        reason: 'Invalid timestamp in cookie',
        hoursElapsed: null,
        remainingHours: null,
        shouldLogout: true, // Signal that logout should be triggered
      };
    }

    const currentTime = Date.now();
    const timeDifference = currentTime - sessionTime;
    const hoursElapsed = timeDifference / (1000 * 60 * 60);
    const testDurationHours = REMEMBER_ME_SESSION_IDLE_DURATION_HOURS; // Use centralized constant from config
    const remainingHours = testDurationHours - hoursElapsed;

    const isExpired = hoursElapsed >= testDurationHours;

    // Auto-cleanup expired cookies if requested
    if (isExpired && autoCleanup) {
      console.log(`${AUTO_CLEANING_PREFIX} expired session cookie`);

      // For expired sessions, completely remove the cookie (no domain exclusion)
      // Domain exclusion is only for manual logouts
      const cleanupResult = removeSessionCookie();

      return {
        isExpired,
        exists: false, // Cookie was removed
        reason: 'Session timeout exceeded - cookie auto-removed',
        hoursElapsed: Math.round(hoursElapsed * 100) / 100,
        remainingHours: 0,
        isExcluded: false,
        autoCleanedUp: true,
        cleanupResult,
        shouldLogout: true, // Signal that logout should be triggered by IdleTracking component
      };
    }

    return {
      isExpired,
      exists: true,
      reason: isExpired ? 'Session timeout exceeded' : 'Session still valid',
      hoursElapsed: Math.round(hoursElapsed * 100) / 100, // Round to 2 decimal places
      remainingHours: Math.max(0, Math.round(remainingHours * 100) / 100), // Round to 2 decimal places, never negative
      isExcluded: false,
      autoCleanedUp: false,
      shouldLogout: isExpired, // Signal logout only if expired
    };
  } catch (error) {
    // Handle cases where cookie exists but isn't valid JSON (legacy format or corrupted)
    // Auto-cleanup corrupted cookies if requested
    if (autoCleanup) {
      console.log(`${AUTO_CLEANING_PREFIX} corrupted session cookie (invalid JSON)`);
      const cleanupResult = removeSessionCookie();

      return {
        isExpired: true,
        exists: false, // Cookie was removed
        reason: 'Invalid JSON format in cookie - auto-removed',
        hoursElapsed: null,
        remainingHours: null,
        autoCleanedUp: true,
        cleanupResult,
        shouldLogout: true, // Signal that logout should be triggered
      };
    }

    return {
      isExpired: true,
      exists: true,
      reason: 'Invalid JSON format in cookie',
      hoursElapsed: null,
      remainingHours: null,
      autoCleanedUp: false,
      shouldLogout: true, // Signal that logout should be triggered
    };
  }
};

/**
 * Utility function to remove the session tracking cookie completely
 * This is used for idle timeout - does NOT exclude domains, just removes the cookie
 * @returns {object} Object containing removal status and details
 */
// Constants for status messages
const COOKIE_REMOVED_MESSAGE = 'Cookie was already removed or never existed';

export const removeSessionCookie = () => {
  // Check if cookie exists before attempting removal
  const currentCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  if (!currentCookie) {
    return {
      success: true,
      reason: COOKIE_REMOVED_MESSAGE,
      cookieExisted: false,
    };
  }

  try {
    // Parse the cookie to determine which domain it's set on
    let cookieData;
    let cookieDomain;
    try {
      cookieData = JSON.parse(currentCookie);
      cookieDomain = cookieData.domain;
    } catch (parseError) {
      // If cookie isn't JSON or doesn't have domain info, assume current domain
      cookieDomain = window.location.host;
    }

    console.log(`Removing session cookie completely from domain: ${cookieDomain}`);

    // Remove from both the original domain and current domain to be safe
    jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY);
    jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: cookieDomain });

    // Verify removal was successful
    const stillExists = !!jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

    if (stillExists) {
      console.warn('Session cookie removal may not have been complete');
      return {
        success: false,
        reason: 'Cookie still exists after removal attempt',
        cookieExisted: true,
      };
    }

    console.log('Session cookie successfully removed completely');
    return {
      success: true,
      reason: 'Cookie successfully removed completely',
      cookieExisted: true,
    };
  } catch (error) {
    console.error('Error removing session cookie:', error);
    return {
      success: false,
      reason: `Error during removal: ${error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE}`,
      cookieExisted: true,
    };
  }
};

/**
 * Mark session cookie as expired due to idle timeout
 * Instead of immediately removing, mark as expired so other domains can detect the logout
 * Cookie will auto-expire after 5 minutes to allow cross-domain coordination
 * @returns {object} Object containing status and details
 */
export const markSessionAsIdleExpired = () => {
  console.log('� [IdleTracking] Marking session as idle expired');

  const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
  if (!sessionCookie) {
    console.log('🔍 [IdleTracking] No session cookie found to mark as expired');
    return;
  }

  console.log('🔍 [IdleTracking] Current session cookie before marking:', sessionCookie);

  try {
    const sessionData = JSON.parse(sessionCookie);

    // Set the idle timeout marker with coordination timestamp
    sessionData.logoutReason = 'idle_timeout';
    sessionData.logoutTimestamp = new Date().toISOString();
    // Add multiple coordination flags to help other domains detect this change quickly
    sessionData.coordinationFlag = Date.now(); // Unique timestamp for change detection
    sessionData.idleTimeoutFlag = true; // Explicit flag for idle timeout
    sessionData.lastUpdated = Date.now(); // Track when this was last modified

    console.log('⚰️ [IdleTracking] Session marked with idle timeout:', sessionData);

    // Update the cookie on the root domain
    const rootDomain = `.${window.location.host.split('.').slice(-2).join('.')}`;
    jsCookie.set(REMEMBER_ME_SESSION_TRACKING_KEY, JSON.stringify(sessionData), {
      domain: rootDomain,
      expires: 1, // 1 day
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
    });

    // Verify the cookie was updated
    const verificationCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
    console.log('🔍 [IdleTracking] Cookie after marking as expired:', verificationCookie);

    console.log('🏁 [IdleTracking] Session cookie updated with idle timeout marker and coordination flag');
  } catch (error) {
    console.error('❌ [IdleTracking] Failed to mark session as idle expired:', error);
  }
};

/**
 * Utility function to handle manual logout - adds current domain to exclusion list
 * This is used when user manually logs out from a specific domain
 * @returns {object} Object containing removal status and details
 */
export const removeSessionCookieWithDomainExclusion = () => {
  // Check if cookie exists before attempting removal
  const currentCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  if (!currentCookie) {
    return {
      success: true,
      reason: COOKIE_REMOVED_MESSAGE,
      cookieExisted: false,
    };
  }

  try {
    // Parse the cookie to determine which domain it's set on
    let cookieData;
    let cookieDomain;
    let cookieEnv;
    try {
      cookieData = JSON.parse(currentCookie);
      cookieDomain = cookieData.domain;
      cookieEnv = cookieData.env;
    } catch (parseError) {
      // If cookie isn't JSON or doesn't have domain info, assume current domain
      cookieDomain = window.location.host;
    }

    const { checkIfRootDomain } = getClientEnv();

    // Determine if the cookie is set on root domain
    const isRootDomain = checkIfRootDomain(cookieDomain, cookieEnv);

    console.log(`Manual logout: Cookie on ${isRootDomain ? 'root' : 'current'} domain (${cookieDomain})`);

    if (isRootDomain) {
      // Case 1: Cookie is on root domain - mark current domain as excluded
      console.log(`Cookie is set on root domain (${cookieDomain}), marking current domain as excluded...`);

      const exclusionResult = markCurrentDomainAsExcluded(currentCookie);

      if (exclusionResult.success && exclusionResult.updatedCookieValue) {
        // Update the cookie with the excluded domain list
        jsCookie.set(REMEMBER_ME_SESSION_TRACKING_KEY, exclusionResult.updatedCookieValue, {
          domain: cookieDomain,
          secure: false,
          sameSite: 'Lax',
          expires: REMEMBER_ME_SESSION_TRACKING_DAYS,
        });

        console.log(`✓ Successfully marked ${exclusionResult.excludedDomain} as excluded`);
        console.log(`Total excluded domains: ${exclusionResult.totalExcluded}`);

        return {
          success: true,
          reason: 'Current domain marked as excluded in root domain cookie',
          cookieExisted: true,
          wasRootDomain: true,
          excludedDomain: exclusionResult.excludedDomain,
          totalExcluded: exclusionResult.totalExcluded,
        };
      } else {
        console.warn(`✗ Failed to mark domain as excluded: ${exclusionResult.error}`);
        // Fallback: remove the cookie completely
        jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: cookieDomain });
        jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: globalThis?.window?.location?.host });

        return {
          success: true,
          reason: 'Cookie removed completely due to exclusion failure',
          cookieExisted: true,
          wasRootDomain: true,
        };
      }
    } else {
      // Case 2: Cookie is on current domain - just remove it
      console.log(`Cookie is set on current domain (${cookieDomain}), removing...`);

      jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY);
      jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: cookieDomain });

      // Verify removal was successful
      const stillExists = !!jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

      if (stillExists) {
        console.warn('Session cookie removal may not have been complete');
        return {
          success: false,
          reason: 'Cookie still exists after removal attempt',
          cookieExisted: true,
          wasRootDomain: false,
        };
      }

      console.log('Session cookie successfully removed from current domain');
      return {
        success: true,
        reason: 'Cookie successfully removed from current domain',
        cookieExisted: true,
        wasRootDomain: false,
      };
    }
  } catch (error) {
    console.error('Error removing session cookie:', error);
    return {
      success: false,
      reason: `Error during removal: ${error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE}`,
      cookieExisted: true,
    };
  }
};

/**
 * Public utility function to check if current domain is excluded from session tracking
 * @returns {boolean} True if current domain should not track idle state
 */
export const isCurrentDomainExcludedFromSession = (): boolean => {
  const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  if (!sessionCookie) {
    return false; // No cookie means no exclusion
  }

  return isCurrentDomainExcluded(sessionCookie);
};

/**
 * Utility function to clean up expired or corrupted session cookies
 * This is useful for periodic cleanup or when you want to force cleanup WITHOUT logout
 * @returns {object} Object containing cleanup results
 */
export const cleanupExpiredSessionCookies = () => {
  const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

  if (!sessionCookie) {
    return {
      success: true,
      reason: 'No cookie to clean up',
      hadCookie: false,
    };
  }

  try {
    // Check if cookie is expired or corrupted
    const cookieData = JSON.parse(sessionCookie);
    const sessionTime = parseInt(cookieData.timestamp, 10);

    if (isNaN(sessionTime)) {
      // Corrupted timestamp - remove it
      console.log('Cleaning up corrupted session cookie (invalid timestamp)');
      const removeResult = removeSessionCookie();
      return {
        success: true,
        reason: 'Corrupted cookie cleaned up',
        hadCookie: true,
        wasCorrupted: true,
        removeResult,
      };
    }

    const currentTime = Date.now();
    const timeDifference = currentTime - sessionTime;
    const hoursElapsed = timeDifference / (1000 * 60 * 60);
    const testDurationHours = REMEMBER_ME_SESSION_IDLE_DURATION_HOURS; // Use centralized constant from config
    const isExpired = hoursElapsed >= testDurationHours;

    if (isExpired) {
      // Expired cookie - remove it (this is manual cleanup, not auto-logout)
      console.log(`Manual cleanup of expired session cookie (${Math.round(hoursElapsed * 100) / 100} hours old)`);
      const removeResult = removeSessionCookie();
      return {
        success: true,
        reason: 'Expired cookie manually cleaned up',
        hadCookie: true,
        wasExpired: true,
        hoursElapsed: Math.round(hoursElapsed * 100) / 100,
        removeResult,
      };
    }

    // Cookie is valid - no cleanup needed
    return {
      success: true,
      reason: 'Cookie is still valid - no cleanup needed',
      hadCookie: true,
      wasExpired: false,
      wasCorrupted: false,
      hoursElapsed: Math.round(hoursElapsed * 100) / 100,
    };
  } catch (error) {
    // Corrupted JSON - remove it
    console.log('Cleaning up corrupted session cookie (invalid JSON)');
    const removeResult = removeSessionCookie();
    return {
      success: true,
      reason: 'Corrupted JSON cookie cleaned up',
      hadCookie: true,
      wasCorrupted: true,
      removeResult,
    };
  }
};

/**
 * Utility function to check if session is expired WITHOUT auto-cleanup
 * This is useful when you want to check expiration without triggering logout
 * @returns {object} Object containing expiration status and details
 */
export const checkSessionExpiration = () => {
  return isSessionCookieExpired(false); // Disable auto-cleanup
};
